import React, { useEffect, useMemo } from 'react';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { FormattedMessage } from 'react-intl';
import { Header } from '../../../../../../../../../components/organisms/Header/Header';
import {
  createSubmitCredentialsScreen,
  SubmitCredentialsScreenCreatorProps,
} from '../../../../../../../../../components/screens/molecules/SubmitCredentialsScreen/components/SubmitCredentialsScreenCreator/SubmitCredentialsScreenCreator';
import { useShouldShowUserHintId } from '../../../../../../../../../components/screens/SubmitCredentials/hooks/useShouldShowUserHintId';

import { PageView } from '../../../../../../../../../models/analytics';
import { parentMessenger } from '../../../../../../../../../core/Analytics/ParentApplicationEventMessenger';
import { Provider } from '../../../../../../../../../models/provider';
import { SubmitCredentialsBase } from '../../../../../../../../../components/screens/molecules/SubmitCredentialsScreen/components/SubmitCredentialsBase/SubmitCredentialsBase';

import {
  AccountSelectionTemplateNotice,
  SourceAccountSelectionTemplateNotice,
  TemplateNotice,
} from '../TemplateNotice/TemplateNotice';
import { messages as submitButtonMessages } from '../../../../../../../../../components/screens/SubmitCredentials/messages';
import { LoginTemplatePayScreenView } from './LoginTemplatePayScreenView';

export const getLoginTemplateHeader: SubmitCredentialsScreenCreatorProps['getHeader'] = (
  onBack
) => {
  return ({ provider, ...headerProps }) => (
    <Header addBottomMargin={false} {...headerProps} onBack={onBack} />
  );
};

export const getLoginTemplateForm: SubmitCredentialsScreenCreatorProps['getForm'] =
  (onSubmit) =>
  ({
    credentialsError,
    userHintId,
    isClientVerified,
    isCredentialsLoading,
    fixedValue,
    provider,
    sourceAccountNumber,
  }) => {
    const { isDesktop } = userAgent;
    const { shouldShowUserHintId } = useShouldShowUserHintId();

    const isSplitFieldsProvider = useMemo(
      () => Provider.isMarketDE(provider) && Provider.isSplitFieldsProvider(provider),
      [provider]
    );

    useEffect(() => {
      if (credentialsError) {
        parentMessenger.fireErrorEvent({
          pageView: PageView.SUBMIT_CREDENTIALS_SCREEN,
          errorMessage: credentialsError,
        });
      }
    }, [credentialsError]);

    let bodyBlock = <TemplateNotice />;
    let buttonLabel = <FormattedMessage {...submitButtonMessages.agree} />;

    if (isDesktop) {
      bodyBlock = <AccountSelectionTemplateNotice />;
      buttonLabel = <FormattedMessage {...submitButtonMessages.continue} />;
    }

    if (sourceAccountNumber) {
      bodyBlock = <SourceAccountSelectionTemplateNotice />;
      buttonLabel = <FormattedMessage {...submitButtonMessages.continue} />;
    }

    return (
      <LoginTemplatePayScreenView
        credentialsError={credentialsError}
        userHintId={userHintId}
        isClientVerified={isClientVerified}
        isCredentialsLoading={isCredentialsLoading}
        fixedValue={fixedValue}
        provider={provider}
        sourceAccountNumber={sourceAccountNumber}
        isDesktop={isDesktop}
        shouldShowUserHintId={shouldShowUserHintId}
        isSplitFieldsProvider={isSplitFieldsProvider}
        onSubmit={(fields) => {
          onSubmit({ fields });
        }}
        bodyBlock={bodyBlock}
        buttonLabel={buttonLabel}
      />
    );
  };

export const LoginTemplatePayScreen = createSubmitCredentialsScreen({
  SubmitCredentialsBase,
  getHeader: getLoginTemplateHeader,
  getForm: getLoginTemplateForm,
});
